import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatLicensePlate } from "@/lib/vehicle";
import type { VehicleFieldKey } from "@/lib/vehicleFields";
import type { Booking, BookingOnlineAddon } from "@/types/camping";

export function guestDisplayName(
  guest:
    | {
        title?: string | null;
        first_name: string;
        middle_name?: string | null;
        last_name: string;
      }
    | null
    | undefined,
  fallback?: string | null,
): string {
  if (!guest) return fallback?.trim() || "Unbekannter Gast";
  const first = [guest.first_name, guest.middle_name].filter(Boolean).join(" ");
  const core = `${guest.last_name}, ${first}`;
  return guest.title?.trim() ? `${guest.title.trim()} ${core}` : core;
}

export function formatGuestFieldValue(key: string, value: string): string {
  if (key === "date_of_birth") return formatIsoDateShort(value);
  return value;
}

const PRIMARY_GUEST_DOB_NOTE_PREFIX = "Geburtsdatum Hauptreisende/r:";

/** Entfernt veraltete Geburtsdatum-Zeilen aus Buchungsnotizen (früher als Notiz gespeichert). */
export function displayBookingNotes(notes: string | null | undefined): string | null {
  if (!notes?.trim()) return null;
  const filtered = notes
    .split("\n")
    .filter((line) => !line.trim().startsWith(PRIMARY_GUEST_DOB_NOTE_PREFIX))
    .join("\n")
    .trim();
  return filtered || null;
}

export function formatWantsElectricity(value: boolean | undefined): string | null {
  if (value === undefined) return null;
  return value ? "Ja" : "Nein";
}

export function formatBookingSource(source: string | undefined | null): string | null {
  if (!source?.trim()) return null;
  if (source === "online") return "Online-Buchung";
  return source;
}

export function vehicleFieldValue(booking: Booking, key: VehicleFieldKey): string | null {
  switch (key) {
    case "license_plate": {
      const formatted = formatLicensePlate(booking.license_plate, booking.license_plate_country);
      return formatted !== "—" ? formatted : null;
    }
    case "vehicle_length_m":
      return booking.vehicle_length_m ? `${booking.vehicle_length_m} m` : null;
    case "vehicle_width_m":
      return booking.vehicle_width_m ? `${booking.vehicle_width_m} m` : null;
    case "vehicle_height_m":
      return booking.vehicle_height_m ? `${booking.vehicle_height_m} m` : null;
    case "vehicle_weight_kg":
      return booking.vehicle_weight_kg ? `${booking.vehicle_weight_kg} kg` : null;
    case "tent_size":
      return booking.tent_size?.trim() || null;
    default:
      return null;
  }
}

export function formatBookingAddons(addons: BookingOnlineAddon[] | null | undefined): string[] {
  if (!addons?.length) return [];
  return addons
    .filter((addon) => (addon.quantity ?? 0) > 0)
    .map((addon) => `${addon.quantity}× ${addon.article_name?.trim() || "Zusatzartikel"}`);
}
